import React, { useState } from "react";
import { Modal, Button, List, Typography, Avatar, Space } from "antd";
import { Link } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CancelFreightDialog from "../../view/frete-copy/components/CancelFreightDialog";
import {
  canCancelFreightStatus,
  resolveFreightStatusCode,
} from "../../util/freight/cancelFreight";

const theme = {
  colors: {
    background: "#F9FAFB",
    surface: "#FFFFFF",
    primary: "#012442",
    primaryBorder: "rgba(1, 36, 66, 0.12)",
    textPrimary: "#1E2124",
    textSecondary: "rgba(30, 33, 36, 0.72)",
    highlight: "#FF3100",
    highlightSoft: "rgba(255, 49, 0, 0.10)",
    white: "#FFFFFF",
  },
  typography: {
    fontFamily:
      "'Airbnb Cereal VF', 'Circular', -apple-system, BlinkMacSystemFont, 'Roboto', 'Helvetica Neue', sans-serif",
    weightRegular: 400,
    weightSemibold: 600,
    titleSize: "1rem",
    bodySize: "0.875rem",
    captionSize: "0.75rem",
  },
  spacing: {
    xxs: 4,
    sm: 12,
    md: 16,
    lg: 24,
  },
  radius: {
    medium: 12,
    large: 16,
  },
  elevation: {
    base: "0px 3px 3px rgba(0,14,51,0.25), 0px 1px 2px rgba(0,14,51,0.25)",
  },
};

function getTimestampDate(timestamp) {
  if (!timestamp) return null;
  if (typeof timestamp.toDate === "function") return timestamp.toDate();
  if (timestamp instanceof Date) return timestamp;
  const seconds =
    typeof timestamp.seconds === "number"
      ? timestamp.seconds
      : timestamp._seconds;
  if (typeof seconds === "number") return new Date(seconds * 1000);
  return null;
}

function formatTimestamp(timestamp) {
  const date = getTimestampDate(timestamp);
  return date ? date.toLocaleString("pt-BR") : "--";
}

export function mapTransportQueueItem(item) {
  if (!item || typeof item !== "object") {
    return { idTransport: "", name: "—", cnpj: "", registeredAt: null };
  }

  const clientPayment =
    item.clientPayment && typeof item.clientPayment === "object"
      ? item.clientPayment
      : {};

  return {
    idTransport:
      item.idTransport ||
      item.idCarrier ||
      item.transportId ||
      item.uidShipper ||
      clientPayment.uid ||
      item.uid ||
      item.id ||
      "",
    name:
      item.name ||
      item.nameCarrier ||
      item.transportName ||
      item.shipperName ||
      clientPayment.name ||
      "—",
    cnpj: item.cnpj || clientPayment.cnpj || "",
    type: item.type || "Transportadora",
    registeredAt:
      item.registrationInLineTime ||
      item.registeredAt ||
      item.timestamp ||
      item.createdAt ||
      null,
  };
}

export function parseTransportQueueResponse(response) {
  const root = response?.data;
  if (Array.isArray(root)) return root.map(mapTransportQueueItem);
  if (Array.isArray(root?.data)) return root.data.map(mapTransportQueueItem);
  if (Array.isArray(root?.items)) return root.items.map(mapTransportQueueItem);
  return [];
}

const actionButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 36,
  height: 36,
  borderRadius: theme.radius.medium,
  flexShrink: 0,
};

const DialogFilaTransport = ({
  transportsInFila,
  openModal,
  handleClose,
  acceptingTransportId,
  onAcceptTransport,
  canAccept = true,
  actionMessage = null,
  freightId = null,
  freightStatus = null,
  canCancelFreight = false,
  onCancelFreight,
  cancelLoading = false,
}) => {
  const list = Array.isArray(transportsInFila) ? transportsInFila : [];
  const [openCancelDialog, setOpenCancelDialog] = useState(false);

  const statusCode = resolveFreightStatusCode(freightStatus);
  const showCancelButton =
    Boolean(freightId) &&
    canCancelFreight &&
    statusCode !== "08" &&
    canCancelFreightStatus(statusCode);

  const handleCancelConfirm = async (reason) => {
    if (!onCancelFreight) return;
    await onCancelFreight(reason);
    setOpenCancelDialog(false);
  };

  const footerButtons = [
    showCancelButton ? (
      <Button
        key="cancel-freight"
        danger
        onClick={() => setOpenCancelDialog(true)}
        disabled={Boolean(acceptingTransportId) || cancelLoading}
        style={{
          borderRadius: theme.radius.medium,
          minWidth: 120,
          height: 40,
          marginRight: "auto",
        }}
      >
        Cancelar frete
      </Button>
    ) : (
      <span key="cancel-spacer" style={{ marginRight: "auto" }} />
    ),
    <Button
      key="ok"
      type="primary"
      onClick={handleClose}
      disabled={Boolean(acceptingTransportId) || cancelLoading}
      style={{
        backgroundColor: theme.colors.primary,
        borderColor: theme.colors.primary,
        color: theme.colors.white,
        borderRadius: theme.radius.medium,
        minWidth: 96,
        height: 40,
      }}
    >
      OK
    </Button>,
  ];

  return (
    <>
      <Modal
      title={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: theme.spacing.sm,
            fontFamily: theme.typography.fontFamily,
          }}
        >
          <div
            style={{
              width: 6,
              height: 24,
              borderRadius: 999,
              background: theme.colors.highlight,
              boxShadow: theme.elevation.base,
            }}
          />
          <span
            style={{
              color: theme.colors.primary,
              fontWeight: theme.typography.weightSemibold,
              fontSize: theme.typography.titleSize,
            }}
          >
            Transportadoras na Fila
          </span>
        </div>
      }
      open={openModal}
      onCancel={handleClose}
      centered
      styles={{
        content: {
          backgroundColor: theme.colors.background,
          boxShadow: theme.elevation.base,
          borderRadius: theme.radius.large,
          padding: theme.spacing.lg,
          fontFamily: theme.typography.fontFamily,
          overflow: "hidden",
        },
        header: {
          backgroundColor: theme.colors.background,
          borderBottom: `1px solid ${theme.colors.primaryBorder}`,
          marginBottom: theme.spacing.md,
          paddingBottom: theme.spacing.md,
        },
        body: {
          paddingTop: theme.spacing.xxs,
        },
        footer: {
          borderTop: `1px solid ${theme.colors.primaryBorder}`,
          marginTop: theme.spacing.lg,
          paddingTop: theme.spacing.md,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: theme.spacing.sm,
        },
      }}
      footer={footerButtons}
    >
      {actionMessage?.text ? (
        <div
          style={{
            marginBottom: theme.spacing.md,
            padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
            borderRadius: theme.radius.medium,
            background:
              actionMessage.severity === 'error'
                ? 'rgba(255, 49, 0, 0.08)'
                : 'rgba(34, 197, 94, 0.12)',
            border: `1px solid ${
              actionMessage.severity === 'error'
                ? 'rgba(255, 49, 0, 0.2)'
                : 'rgba(34, 197, 94, 0.25)'
            }`,
            color:
              actionMessage.severity === 'error'
                ? theme.colors.highlight
                : '#15803d',
            fontSize: theme.typography.bodySize,
          }}
        >
          {actionMessage.text}
        </div>
      ) : null}
      {list.length < 1 ? (
        <div
          style={{
            background: theme.colors.surface,
            border: `1px solid ${theme.colors.primaryBorder}`,
            borderRadius: theme.radius.large,
            padding: theme.spacing.lg,
            boxShadow: theme.elevation.base,
          }}
        >
          <Typography.Text
            style={{
              display: "block",
              color: theme.colors.textSecondary,
              textAlign: "center",
            }}
          >
            Não há transportadora na fila deste frete no momento.
          </Typography.Text>
        </div>
      ) : (
        <div
          style={{
            background: theme.colors.surface,
            border: `1px solid ${theme.colors.primaryBorder}`,
            borderRadius: theme.radius.large,
            boxShadow: theme.elevation.base,
            overflow: "hidden",
          }}
        >
          <List
            dataSource={list}
            split={false}
            renderItem={(item, index) => {
              const isAccepting = acceptingTransportId === item.idTransport;

              return (
                <List.Item
                  style={{
                    padding: `${theme.spacing.md}px ${theme.spacing.lg}px`,
                    borderBottom:
                      index !== list.length - 1
                        ? `1px solid ${theme.colors.primaryBorder}`
                        : "none",
                    background: theme.colors.surface,
                  }}
                  actions={[
                    <Space key="actions" size={8}>
                      {item.idTransport ? (
                        <Link
                          to={`/newEmbarcador/${item.idTransport}`}
                          state={{ viewOnly: true }}
                          style={{
                            ...actionButtonStyle,
                            color: theme.colors.highlight,
                            background: theme.colors.highlightSoft,
                            border: "1px solid rgba(255, 49, 0, 0.16)",
                            textDecoration: "none",
                          }}
                          aria-label={`Visualizar transportadora ${item.name}`}
                        >
                          <VisibilityIcon style={{ fontSize: 18 }} />
                        </Link>
                      ) : null}
                      {canAccept && item.idTransport ? (
                        <Button
                          type="primary"
                          size="small"
                          loading={isAccepting}
                          disabled={
                            Boolean(acceptingTransportId) && !isAccepting
                          }
                          onClick={() => onAcceptTransport?.(item)}
                          style={{
                            backgroundColor: theme.colors.primary,
                            borderColor: theme.colors.primary,
                            borderRadius: theme.radius.medium,
                            fontWeight: theme.typography.weightSemibold,
                          }}
                        >
                          Aceitar
                        </Button>
                      ) : null}
                    </Space>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        style={{
                          backgroundColor: theme.colors.highlightSoft,
                          color: theme.colors.highlight,
                          border: "1px solid rgba(255, 49, 0, 0.18)",
                          fontWeight: theme.typography.weightSemibold,
                        }}
                      >
                        {item.name?.[0]?.toUpperCase() || "T"}
                      </Avatar>
                    }
                    title={
                      <Typography.Text
                        style={{
                          color: theme.colors.textPrimary,
                          fontWeight: theme.typography.weightSemibold,
                        }}
                      >
                        {item.name}
                      </Typography.Text>
                    }
                    description={
                      <span
                        style={{
                          display: "block",
                          marginTop: theme.spacing.xxs,
                          color: theme.colors.textSecondary,
                          fontSize: theme.typography.captionSize,
                        }}
                      >
                        {item.cnpj ? (
                          <>
                            CNPJ: {item.cnpj}
                            <br />
                          </>
                        ) : null}
                        Na fila desde: {formatTimestamp(item.registeredAt)}
                      </span>
                    }
                  />
                </List.Item>
              );
            }}
          />
        </div>
      )}
      </Modal>

      <CancelFreightDialog
        open={openCancelDialog}
        loading={cancelLoading}
        onClose={() => setOpenCancelDialog(false)}
        onConfirm={handleCancelConfirm}
        message="O frete será cancelado e sairá da lista de disponíveis. Transportadoras na fila serão notificadas."
      />
    </>
  );
};

export default DialogFilaTransport;
